import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import { withRouter } from "react-router-dom";

import Jumbotron from "../components/common/Jumbotron";
import Layout from "../components/common/Layout";
import Details from "../components/sections/Details";
import TOCSection from "../components/sections/Index";
import SearchSection from "../components/sections/Search";
import usePatentEndpoint from "../customHooks/usePatentEndpoint";
import ArrayOfYears from "../utils/ArrayOfYears";

const GazettePage = (props) => {
  const { content, type, breadCrumbs } = props;
  document.title = content.title;

  const contentRef = useRef(null);
  const { search, postSearch } = usePatentEndpoint();
  const yearOptions = ArrayOfYears("2022");

  const defaultIssueOptions = [...Array(52).keys()]
    .map((e) => ({
      value: e + 1,
      title: `${content.common.week} ${e + 1}`,
    }))
    .reverse();
  const [issueOptions, setIssueOptions] = useState(defaultIssueOptions);

  const [year, setYear] = useState(null);
  const [issue, setIssue] = useState(null);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startValid, setStartValid] = useState(true);
  const [endValid, setEndValid] = useState(true);

  const [rangeSearch, setRangeSearch] = useState(false);
  const [warning, setWarning] = useState(false);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const currentYear = moment().year().toString();
    const currentWeek = parseInt(moment().format("W"));

    // year last days fix for week 1
    const isCurrentYear = year === currentYear;
    const isLastMonth = moment().month() === 11;
    const isFutureYear = year > currentYear;
    const isFirstWeekAndLastMonth = currentWeek === 1 && isLastMonth;

    if ((isCurrentYear && !isFirstWeekAndLastMonth) || isFutureYear) {
      setIssueOptions(
        [...Array(currentWeek).keys()]
          .map((e) => ({
            value: e + 1,
            title: `${content.common.week} ${e + 1}`,
          }))
          .reverse(),
      );
      if (issue > currentWeek) {
        setIssue(moment().format("W"));
      }
    } else {
      setIssueOptions(defaultIssueOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, issue, props.content]);

  const searchButton = useCallback(() => {
    if (startDate && endDate) {
      setRangeSearch(true);
      postSearch({ startDate, endDate, type });
      setSearching(true);
      setWarning(false);
      return;
    }

    if (year && issue) {
      setWarning(false);
      setRangeSearch(false);
      postSearch({ year, issue, type });
      setSearching(true);
      return;
    }

    setWarning(true);
    setSearching(false);
  }, [startDate, endDate, year, issue, type, postSearch]);

  const [gazettes, setGazettes] = useState([]);
  useEffect(() => {
    if (search?.data?.gazetteGroups?.length > 0) {
      setGazettes(search.data.gazetteGroups);
    } else {
      setGazettes([]);
    }
  }, [search, contentRef]);

  const handleReset = () => {
    resetYearAndIssue();
    resetDates();
    setWarning(false);
  };

  const resetYearAndIssue = () => {
    setYear(null);
    setIssue(null);
    setGazettes([]);
    setSearching(false);
  };

  const resetDates = () => {
    setStartDate("");
    setEndDate("");
    setGazettes([]);
    setSearching(false);
  };

  return (
    <Layout breadCrumbs={breadCrumbs} type={type}>
      <main id="maincontent" tabIndex="-1">
        <Jumbotron content={content} />
        <Container>
          <SearchSection
            content={content}
            yearOptions={yearOptions}
            issueOptions={issueOptions}
            onYearChange={(e) => {
              if (e !== "0") resetDates();
              if (e === "0") setYear(null);
              else setYear(e);
              setWarning(false);
            }}
            onIssueChange={(e) => {
              if (e !== "0") resetDates();
              if (e === "0") setIssue(null);
              else setIssue(e);
              setWarning(false);
            }}
            onStartDateChange={(e, a) => {
              if (a != null) resetYearAndIssue();
              setStartDate(new Date(a.currentTarget.value).toISOString().split("T")[0]);
              setStartValid(true);
            }}
            onEndDateChange={(e, a) => {
              if (a != null) resetYearAndIssue();
              setEndDate(new Date(a.currentTarget.value).toISOString().split("T")[0]);
              setEndValid(true);
            }}
            startValid={startValid}
            endValid={endValid}
            startDate={startDate}
            endDate={endDate}
            onSubmit={searchButton}
            onReset={handleReset}
            issue={issue}
            year={year}
            warning={warning}
          />
          <div>
            {searching && gazettes && ((year && issue) || (startDate && endDate)) && (
              <TOCSection
                id="tosection"
                rangeSearch={rangeSearch}
                content={content}
                searchResults={gazettes}
                startDate={startDate}
                gazettes={gazettes}
                endDate={endDate}
                issue={issue}
                year={year}
                loading={search.loading}
                error={search.error}
              />
            )}
            {gazettes.length > 0 &&
              gazettes.map((e, i) => (
                <Details
                  key={i}
                  id={e.type}
                  content={content}
                  gazettes={e.gazettes}
                  type={e.gazetteType.toLowerCase()}
                />
              ))}
          </div>
        </Container>
      </main>
    </Layout>
  );
};

export default withRouter(GazettePage);
